import React, { useState } from 'react';
import { supabase, seedUserDefaults } from '../../lib/supabase';
import { Eye, EyeOff, Shield, TrendingUp, Lock, Mail, User, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

// ─── Shared helpers ────────────────────────────────────────────────────────────

function InputField({ label, type = 'text', value, onChange, placeholder, icon: Icon, error, hint, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon size={16} style={{
            position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted)', pointerEvents: 'none'
          }} />
        )}
        <input
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="form-input"
          style={{ paddingLeft: Icon ? 42 : 14, paddingRight: isPassword ? 44 : 14 }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="btn-icon"
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 6 }}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {error && <span className="form-error"><AlertCircle size={12} />{error}</span>}
      {hint && !error && <span className="form-hint">{hint}</span>}
    </div>
  );
}

function AuthCard({ children, title, subtitle }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-base)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{
          width: 60, height: 60, borderRadius: 16,
          background: 'linear-gradient(135deg, var(--accent) 0%, #6366F1 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', boxShadow: 'var(--shadow-accent)',
        }}>
          <TrendingUp size={28} color="#fff" />
        </div>
        <h1 style={{ fontSize: '1.625rem', fontWeight: 700, marginBottom: 4 }}>MyFinance</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', letterSpacing: '0.05em' }}>
          PERSONAL FINANCE MANAGER
        </p>
      </div>

      {/* Card */}
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: '28px 24px',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ marginBottom: 4 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '0.875rem' }}>{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Login Screen ──────────────────────────────────────────────────────────────

function LoginScreen({ onSwitchToRegister, onMFARequired, onForgotPassword }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    setError('');

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError) {
      setError(authError.message);
      setLoading(false);
      return;
    }

    // Check if MFA is required
    if (data?.session === null && data?.user === null) {
      onMFARequired();
    }
    setLoading(false);
  }

  return (
    <AuthCard title="Welcome back" subtitle="Sign in to your account">
      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{
            background: 'var(--danger-light)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            color: 'var(--danger)',
            fontSize: '0.875rem',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} />
            {error}
          </div>
        )}

        <InputField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          icon={Mail}
          autoComplete="email"
        />
        <InputField
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Your password"
          icon={Lock}
          autoComplete="current-password"
        />

        {/* Forgot password link */}
        <div style={{ textAlign: 'right', marginTop: -8 }}>
          <button type="button" onClick={onForgotPassword} style={{
            background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8125rem', fontWeight: 500,
          }}>
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary btn-full btn-lg"
          style={{ marginTop: 4 }}
        >
          {loading ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Signing in…</> : <>Sign In <ChevronRight size={18} /></>}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 8 }}>
          Don't have an account?{' '}
          <button type="button" onClick={onSwitchToRegister} style={{
            background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 500
          }}>
            Create account
          </button>
        </p>
      </form>
    </AuthCard>
  );
}

// ─── Register Screen ───────────────────────────────────────────────────────────

function RegisterScreen({ onSwitchToLogin, onRegistered }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!fullName.trim()) e.fullName = 'Name is required';
    if (!email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Invalid email address';
    if (!password) e.password = 'Password is required';
    else if (password.length < 8) e.password = 'Minimum 8 characters';
    else if (!/(?=.*[A-Z])/.test(password)) e.password = 'Must contain an uppercase letter';
    else if (!/(?=.*[0-9])/.test(password)) e.password = 'Must contain a number';
    if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError('');

    const { data, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });

    if (authError) {
      setError(authError.message);
      setLoading(false);
      return;
    }

    // Seed defaults for the new user
    if (data?.user) {
      await seedUserDefaults(data.user.id);
    }

    setSuccess(true);
    setLoading(false);
  }

  if (success) {
    return (
      <AuthCard title="Check your email" subtitle="">
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--success-light)', border: '2px solid var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <CheckCircle size={32} color="var(--success)" />
          </div>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>Registration successful!</p>
          <p style={{ fontSize: '0.875rem', marginBottom: 24 }}>
            We've sent a confirmation link to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>.
            Please check your inbox and verify your email to continue.
          </p>
          <button
            onClick={onSwitchToLogin}
            className="btn btn-primary btn-full"
          >
            Back to Sign In
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create account" subtitle="Start managing your finances today">
      <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && (
          <div style={{
            background: 'var(--danger-light)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-md)', padding: '10px 14px',
            color: 'var(--danger)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} />{error}
          </div>
        )}

        <InputField
          label="Full Name"
          value={fullName}
          onChange={setFullName}
          placeholder="Your full name"
          icon={User}
          error={errors.fullName}
          autoComplete="name"
        />
        <InputField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          icon={Mail}
          error={errors.email}
          autoComplete="email"
        />
        <InputField
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Min 8 chars, 1 uppercase, 1 number"
          icon={Lock}
          error={errors.password}
          autoComplete="new-password"
        />
        <InputField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Repeat your password"
          icon={Lock}
          error={errors.confirmPassword}
          autoComplete="new-password"
        />

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary btn-full btn-lg"
          style={{ marginTop: 8 }}
        >
          {loading ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Creating account…</> : 'Create Account'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 4 }}>
          Already have an account?{' '}
          <button type="button" onClick={onSwitchToLogin} style={{
            background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 500
          }}>
            Sign in
          </button>
        </p>
      </form>
    </AuthCard>
  );
}

// ─── MFA Verification Screen ───────────────────────────────────────────────────

function MFAScreen({ onBack }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [factors, setFactors] = useState([]);
  const [factorId, setFactorId] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [step, setStep] = useState('loading'); // loading | challenge | setup

  React.useEffect(() => {
    async function loadFactors() {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) { setError(error.message); setStep('error'); return; }

      const totp = data?.totp || [];
      setFactors(totp);

      if (totp.length > 0) {
        const fId = totp[0].id;
        setFactorId(fId);
        const { data: challengeData, error: cErr } = await supabase.auth.mfa.challenge({ factorId: fId });
        if (cErr) { setError(cErr.message); setStep('error'); return; }
        setChallengeId(challengeData.id);
        setStep('challenge');
      } else {
        setStep('challenge'); // No factor; just proceed (MFA may be optional)
      }
    }
    loadFactors();
  }, []);

  async function handleVerify(e) {
    e.preventDefault();
    if (!code || code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    if (!factorId || !challengeId) { setError('No MFA factor found. Please contact support.'); return; }
    setLoading(true);
    setError('');

    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code: code.replace(/\s/g, ''),
    });

    if (verifyErr) {
      setError(verifyErr.message);
      setLoading(false);
    }
    // On success, auth state listener in AppContext handles the rest
  }

  if (step === 'loading') {
    return (
      <AuthCard title="Verifying…" subtitle="">
        <div style={{ textAlign: 'center', padding: 32 }}>
          <span className="spinner" style={{ width: 32, height: 32, margin: '0 auto' }} />
        </div>
      </AuthCard>
    );
  }

  if (factors.length === 0) {
    // No MFA set up yet — show setup prompt
    return (
      <AuthCard title="Two-Factor Authentication" subtitle="Secure your account with 2FA">
        <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--accent-light)', border: '2px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Shield size={28} color="var(--accent)" />
          </div>
          <p style={{ marginBottom: 24, fontSize: '0.9rem' }}>
            2FA is available but not yet configured on your account.
            You can set it up in Settings → Security after logging in.
          </p>
          <button onClick={onBack} className="btn btn-primary btn-full">
            Continue to App
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Two-Factor Authentication" subtitle="Enter your authenticator code">
      <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--accent-light)', border: '2px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Shield size={24} color="var(--accent)" />
          </div>
          <p style={{ fontSize: '0.875rem' }}>
            Open your authenticator app and enter the 6-digit code.
          </p>
        </div>

        {error && (
          <div style={{
            background: 'var(--danger-light)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-md)', padding: '10px 14px',
            color: 'var(--danger)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} />{error}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Authentication Code</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="000000"
            className="form-input mono"
            style={{
              textAlign: 'center',
              fontSize: '1.75rem',
              letterSpacing: '0.4em',
              fontWeight: 600,
            }}
            autoComplete="one-time-code"
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="btn btn-primary btn-full btn-lg"
        >
          {loading ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Verifying…</> : 'Verify Code'}
        </button>

        <button type="button" onClick={onBack} className="btn btn-ghost btn-full">
          Back to Sign In
        </button>
      </form>
    </AuthCard>
  );
}

// ─── Forgot Password Screen ────────────────────────────────────────────────────

function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError('');

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      // redirectTo must match the Site URL configured in Supabase Auth settings.
      // Supabase appends #access_token=...&type=recovery to this URL.
      redirectTo: window.location.origin,
    });

    if (resetError) {
      setError(resetError.message);
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  }

  if (sent) {
    return (
      <AuthCard title="Check your email" subtitle="">
        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--success-light)', border: '2px solid var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <CheckCircle size={28} color="var(--success)" />
          </div>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>Reset link sent!</p>
          <p style={{ fontSize: '0.875rem', marginBottom: 24, color: 'var(--text-secondary)' }}>
            We sent a password reset link to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>.
            Check your inbox and click the link — it will open the app and prompt you to set a new password.
          </p>
          <button onClick={onBack} className="btn btn-secondary btn-full">
            Back to Sign In
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Reset Password" subtitle="Enter your email to receive a reset link">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{
            background: 'var(--danger-light)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-md)', padding: '10px 14px',
            color: 'var(--danger)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} />{error}
          </div>
        )}

        <InputField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          icon={Mail}
          autoComplete="email"
          hint="We'll send a reset link to this address."
        />

        <button type="submit" disabled={loading} className="btn btn-primary btn-full btn-lg" style={{ marginTop: 4 }}>
          {loading ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Sending…</> : 'Send Reset Link'}
        </button>

        <button type="button" onClick={onBack} className="btn btn-ghost btn-full">
          Back to Sign In
        </button>
      </form>
    </AuthCard>
  );
}

// ─── Reset Password Screen (shown after clicking the email link) ───────────────

export function ResetPasswordScreen({ onDone }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password) { setError('Please enter a new password.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/(?=.*[A-Z])/.test(password)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/(?=.*[0-9])/.test(password)) { setError('Password must contain at least one number.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    setError('');

    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);
    // Sign out after successful reset so they log in fresh with the new password
    setTimeout(async () => {
      await supabase.auth.signOut();
      onDone();
    }, 2500);
  }

  if (success) {
    return (
      <AuthCard title="Password Updated" subtitle="">
        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--success-light)', border: '2px solid var(--success)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <CheckCircle size={28} color="var(--success)" />
          </div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Password changed successfully!</p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Signing you out so you can log in with your new password…
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set New Password" subtitle="Choose a strong password for your account">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{
            background: 'var(--danger-light)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-md)', padding: '10px 14px',
            color: 'var(--danger)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} />{error}
          </div>
        )}

        <InputField
          label="New Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Min 8 chars, 1 uppercase, 1 number"
          icon={Lock}
          autoComplete="new-password"
        />
        <InputField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Repeat your new password"
          icon={Lock}
          autoComplete="new-password"
        />

        <button type="submit" disabled={loading} className="btn btn-primary btn-full btn-lg" style={{ marginTop: 4 }}>
          {loading
            ? <><span className="spinner" style={{ width: 18, height: 18 }} /> Updating…</>
            : <><Lock size={16} /> Set New Password</>
          }
        </button>
      </form>
    </AuthCard>
  );
}

// ─── Auth Container (router) ───────────────────────────────────────────────────

export default function AuthScreens() {
  const [screen, setScreen] = useState('login'); // login | register | mfa | forgot

  if (screen === 'mfa') return <MFAScreen onBack={() => setScreen('login')} />;
  if (screen === 'register') return (
    <RegisterScreen
      onSwitchToLogin={() => setScreen('login')}
      onRegistered={() => setScreen('login')}
    />
  );
  if (screen === 'forgot') return (
    <ForgotPasswordScreen onBack={() => setScreen('login')} />
  );
  return (
    <LoginScreen
      onSwitchToRegister={() => setScreen('register')}
      onMFARequired={() => setScreen('mfa')}
      onForgotPassword={() => setScreen('forgot')}
    />
  );
}
